/**
 * Le recouvrement, calculé.
 *
 * TOUT LE MODULE TIENT DANS CE FICHIER, et c'est délibéré : l'API ne calcule
 * aucun solde. Elle rend les bases par taux de TVA et la somme des
 * affectations ; le reste dû, la balance âgée, l'acompte d'un client et le
 * niveau de relance dû se déduisent ici.
 *
 * Le TTC passe par `ventilation()` de `facturation.ts` — LE MÊME calcul que la
 * page d'édition d'une facture. Le refaire serait une deuxième implémentation
 * de la même règle, et la première divergence serait invisible.
 *
 * Rien ici ne touche à React : ce sont des fonctions pures, testées seules.
 * C'est ce qui permet de prouver qu'une balance tombe juste sans avoir à
 * monter un écran.
 *
 *
 * LES MONTANTS SONT DES CENTIMES ENTIERS
 * ---------------------------------------------------------------------------
 * Partout, sans exception. Un euro en nombre flottant, additionné trois cents
 * fois dans une balance, finit par ne plus tomber juste — et une balance qui ne
 * tombe pas juste ne sert à rien.
 */

import { montant, totauxDePiece, type BaseTaux } from './facturation.js';

// -----------------------------------------------------------------------------
// Ce que l'API rend
// -----------------------------------------------------------------------------

/** Une pièce émise, telle que `GET /recouvrement/poste` la rend. */
export interface PieceRecouvrement {
  readonly id: string;
  readonly type: string;
  readonly numero: string | null;
  readonly client_id: string;
  readonly client_nom: string;
  readonly date_piece: string;
  readonly echeance: string | null;
  readonly remise_globale: string | null;
  readonly emise_le: string | null;
  readonly envoye_le: string | null;
  /** La somme des affectations. Négative sur un avoir consommé. */
  readonly regle: string | number;
  /**
   * LE JOUR OÙ LA PIÈCE A ÉTÉ RÉGLÉE — le dernier encaissement qui lui est
   * affecté. `null` tant qu'elle ne l'est pas.
   *
   * RIEN N'EST STOCKÉ. L'utilisateur a demandé le 10/09/2026 de « garder en
   * mémoire le temps de retard de chaque client avant paiement » ; la réponse
   * est un calcul, pas une colonne. Un délai rangé à côté des règlements
   * deviendrait faux au premier lettrage corrigé, et personne ne le verrait.
   */
  readonly regle_le: string | null;
  /** Le dernier niveau de relance RÉELLEMENT parti pour cette pièce. */
  readonly relance_niveau: string | null;
  readonly relance_le: string | null;
  readonly bases: readonly BaseTaux[];
}

/** Ce qu'un client a versé sans qu'on l'ait encore imputé. */
export interface Acompte {
  readonly client_id: string;
  readonly client_nom: string;
  readonly montant: string | number;
}

/**
 * Un gel en cours. Un par FACTURE au plus.
 *
 * Corrigé le 10/09/2026 : le gel portait sur le client, il porte sur la pièce.
 * `client_id` est rendu par l'API mais LU DE LA FACTURE — il n'est stocké nulle
 * part sur le gel.
 */
export interface Gel {
  readonly id: string;
  readonly facture_id: string;
  readonly facture_numero?: string | null;
  readonly client_id: string;
  readonly client_nom?: string;
  readonly motif: string;
  readonly reprise_le: string;
  readonly note: string | null;
}

/** Une affectation, telle qu'elle voyage dans les deux sens. */
export interface Affectation {
  readonly facture_id: string;
  readonly numero?: string | null;
  readonly type?: string;
  readonly montant: string | number;
}

/** Un encaissement du journal. */
export interface Encaissement {
  readonly id: string;
  readonly client_id: string;
  readonly client_nom: string;
  readonly recu_le: string;
  readonly mode: string;
  readonly reference: string | null;
  readonly montant: string | number;
  readonly origine: string;
  readonly note: string | null;
  readonly saisi_par: string;
  readonly affecte: string | number;
  readonly affectations: readonly Affectation[];
}

// -----------------------------------------------------------------------------
// Les mots
// -----------------------------------------------------------------------------

/** Les quatre modes validés le 09/09/2026. */
export const MODES: readonly { readonly cle: string; readonly nom: string }[] = [
  { cle: 'virement', nom: 'Virement' },
  { cle: 'cheque', nom: 'Chèque' },
  { cle: 'lcr', nom: 'LCR / traite' },
  { cle: 'prelevement', nom: 'Prélèvement' },
];

export function modeDit(mode: string): string {
  return MODES.find((m) => m.cle === mode)?.nom ?? mode;
}

/** Les cinq motifs de gel : les trois du skill, plus deux de la maquette. */
export const MOTIFS_GEL: readonly { readonly cle: string; readonly nom: string }[] = [
  { cle: 'promesse', nom: 'Promesse de paiement' },
  { cle: 'contestation', nom: 'Facture contestée' },
  { cle: 'echeancier', nom: 'Échéancier accordé' },
  { cle: 'litige', nom: 'Litige transport en cours' },
  { cle: 'autre', nom: 'Autre' },
];

export function motifDit(motif: string): string {
  return MOTIFS_GEL.find((m) => m.cle === motif)?.nom ?? motif;
}

export const CANAUX_RELANCE: readonly { readonly cle: string; readonly nom: string }[] =
  [
    { cle: 'courriel', nom: 'Courriel' },
    { cle: 'courrier', nom: 'Courrier' },
    { cle: 'lrar', nom: 'LRAR' },
  ];

export function canalRelanceDit(canal: string): string {
  return CANAUX_RELANCE.find((c) => c.cle === canal)?.nom ?? canal;
}

/**
 * LA CADENCE — recopiée du skill `relances-clients-tns`, section « Cadence,
 * ancrée sur la date d'échéance (J0) ». Elle n'est PAS inventée ici.
 *
 * RÉSERVE, portée par la maquette et non tranchée : la note de décision du
 * 13/08/2026 dit « 3 étapes entre l'échéance et la mise en demeure », il y en a
 * quatre ici. Le skill est l'outil réellement en service, donc c'est lui qui
 * fait foi tant que l'écran de paramétrage n'existe pas.
 *
 * Elle vit dans le code, comme le format des numéros de facture, et pour la
 * même raison. Le jour où Paramètres › Recouvrement existera, elle en viendra.
 */
export const CADENCE: readonly {
  readonly cle: string;
  readonly nom: string;
  readonly jour: number;
  readonly canal: string;
  /** Les frais sont-ils chiffrés sur ce courrier ? */
  readonly frais: 'non' | 'annonces' | 'chiffres';
}[] = [
  { cle: 'avis', nom: 'Avis de compte', jour: -5, canal: 'courriel', frais: 'non' },
  { cle: 'relance1', nom: 'Relance 1', jour: 2, canal: 'courriel', frais: 'non' },
  { cle: 'relance2', nom: 'Relance 2', jour: 8, canal: 'courriel', frais: 'annonces' },
  { cle: 'relance3', nom: 'Relance 3', jour: 15, canal: 'courriel', frais: 'chiffres' },
  { cle: 'relance4', nom: 'Relance 4', jour: 21, canal: 'courriel', frais: 'chiffres' },
  {
    cle: 'mise_en_demeure',
    nom: 'Mise en demeure',
    jour: 30,
    canal: 'lrar',
    frais: 'chiffres',
  },
  {
    cle: 'action_directe',
    nom: 'Action directe',
    jour: 36,
    canal: 'lrar',
    frais: 'non',
  },
];

export function niveauDit(niveau: string | null): string {
  if (niveau === null) return '—';
  return CADENCE.find((n) => n.cle === niveau)?.nom ?? niveau;
}

// -----------------------------------------------------------------------------
// Les dates
// -----------------------------------------------------------------------------

/** Un jour ISO en nombre de jours depuis l'époque, ou `null` s'il est illisible. */
function enJours(iso: string): number | null {
  const bouts = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(iso);
  if (bouts === null) return null;
  return (
    Date.UTC(Number(bouts[1]), Number(bouts[2]) - 1, Number(bouts[3])) / 86_400_000
  );
}

/** UTC partout : en heure locale, un décalage d'été fait sauter un jour. */
export function ajouterJours(iso: string, jours: number): string | null {
  const base = enJours(iso);
  if (base === null) return null;
  return new Date((base + jours) * 86_400_000).toISOString().slice(0, 10);
}

/** L'écart en jours, signé : positif si `fin` est après `debut`. */
export function ecartJours(debut: string, fin: string): number | null {
  const a = enJours(debut);
  const b = enJours(fin);
  if (a === null || b === null) return null;
  return b - a;
}

// -----------------------------------------------------------------------------
// Ce qu'une pièce doit
// -----------------------------------------------------------------------------

/** Le TTC d'une pièce, en centimes. */
export function ttcDePiece(piece: PieceRecouvrement): number {
  return totauxDePiece({
    id: piece.id,
    type: piece.type,
    numero: piece.numero,
    client_nom: piece.client_nom,
    date_piece: piece.date_piece,
    echeance: piece.echeance,
    remise_globale: piece.remise_globale,
    emise_le: piece.emise_le,
    envoye_le: piece.envoye_le,
    envoye_canal: null,
    bases: piece.bases,
  }).ttc;
}

/**
 * CE QUE LA PIÈCE PORTE AU COMPTE DU CLIENT, en centimes.
 *
 * UN AVOIR COMPTE EN NÉGATIF. C'est ce qui permet de lire ce qu'un client doit
 * vraiment en additionnant une seule liste, sans écran à part — la raison même
 * pour laquelle les avoirs vivent dans la table des factures.
 *
 *
 * ON NE NÉGOCIE PLUS LE SIGNE ICI — CORRIGÉ LE 17/09/2026
 * ---------------------------------------------------------------------------
 * CE QUE CETTE FONCTION FAISAIT, ET CE QUE ÇA A COÛTÉ. Elle rendait
 * `type === 'avoir' ? -ttc : ttc`. C'était une DOUBLE NÉGATION : les lignes
 * d'un avoir portent DÉJÀ un prix unitaire négatif — `factures.ts` les écrit
 * ainsi depuis le 11/09, sur la forme constatée chez Pennylane
 * (`amount: "-504.0"`). La base rendue par l'API est donc négative, et la
 * retourner une seconde fois la rendait POSITIVE.
 *
 * RÉSULTAT EN PRODUCTION : un avoir S'AJOUTAIT à ce que le client devait. Sur
 * la facture VPK du 16/09, le poste client réclamait le double.
 *
 * Signalé par Mohamed le 16/09 : « le module recouvrement poste client a
 * additionné au lieu de soustraire ». Il a cru s'être trompé de saisie, et je
 * l'ai cru aussi — j'avais lu la négation ci-dessus, vu qu'un avoir était
 * traité, et conclu trop vite. JE N'AVAIS PAS REGARDÉ LE SIGNE DE L'ENTRÉE.
 *
 * LE SIGNE EST DANS LA DONNÉE, ET IL DOIT Y RESTER. Le remettre ici en
 * fonction du type, c'était tenir la même vérité à deux endroits : le jour où
 * l'un des deux change, ils se contredisent. C'est exactement ce qui est
 * arrivé le 11/09, quand le prix unitaire est devenu négatif sans que
 * personne ne revienne ici.
 *
 * LA FONCTION RESTE, même devenue triviale : elle NOMME l'intention, et tous
 * les calculs du module passent par elle. La supprimer disperserait
 * `ttcDePiece` dans dix endroits, et le prochain qui voudra traiter un cas
 * particulier n'aurait plus d'endroit où le mettre.
 */
export function porteAuCompte(piece: PieceRecouvrement): number {
  return ttcDePiece(piece);
}

/** En centimes, à partir d'un numérique rendu en texte par le pilote. */
export function centimes(valeur: string | number | null | undefined): number {
  if (valeur === null || valeur === undefined) return 0;
  const nombre = typeof valeur === 'number' ? valeur : Number(valeur);
  if (!Number.isFinite(nombre)) return 0;
  return Math.round(nombre * 100);
}

/**
 * LE RESTE DÛ — jamais stocké, toujours calculé.
 *
 * Ce que la pièce porte, moins ce qui lui est affecté. Sur un avoir, les deux
 * sont négatifs et le reste tend vers zéro à mesure qu'il se consomme.
 */
export function resteDu(piece: PieceRecouvrement): number {
  return porteAuCompte(piece) - centimes(piece.regle);
}

/** Une pièce est soldée quand il ne reste rien — au centime près. */
export function soldee(piece: PieceRecouvrement): boolean {
  return resteDu(piece) === 0;
}

/**
 * LE DÉLAI DE RÈGLEMENT D'UNE PIÈCE, EN JOURS APRÈS SON ÉCHÉANCE.
 *
 * Positif : payée en retard. Négatif : payée d'avance. Zéro : pile le jour dit.
 * `null` quand la question n'a pas de réponse — pièce non soldée, sans
 * échéance, ou sans règlement.
 *
 * DEMANDE DE L'UTILISATEUR DU 10/09/2026, et répondue par un calcul : « on
 * garde en mémoire le temps de retard de chaque client avant paiement pour
 * effectuer nos rapports plus tard ». Rien n'est gardé, tout se déduit — et
 * c'est ce qui garantit que le chiffre reste juste après une correction de
 * lettrage, là où une colonne aurait figé une valeur fausse.
 */
export function delaiDeReglement(piece: PieceRecouvrement): number | null {
  if (piece.regle_le === null || piece.echeance === null) return null;
  if (resteDu(piece) !== 0) return null;
  return ecartJours(piece.echeance, piece.regle_le);
}

/**
 * LE RETARD MOYEN DE RÈGLEMENT d'un jeu de pièces, arrondi au jour.
 *
 * Les pièces non soldées sont IGNORÉES, pas comptées à zéro : une facture
 * qu'on n'a pas encore payée n'est pas une facture payée à l'heure. `null`
 * quand aucune ne compte.
 */
export function retardMoyen(pieces: readonly PieceRecouvrement[]): number | null {
  const delais = pieces
    .map((piece) => delaiDeReglement(piece))
    .filter((jours): jours is number => jours !== null);
  if (delais.length === 0) return null;
  return Math.round(delais.reduce((a, b) => a + b, 0) / delais.length);
}

/** Ce que la colonne « Retard moyen » écrit. */
export function retardMoyenDit(jours: number | null): string {
  if (jours === null) return '—';
  if (jours === 0) return 'à l’échéance';
  if (jours < 0) return `${String(-jours)} j d’avance`;
  return `${String(jours)} j`;
}

// -----------------------------------------------------------------------------
// La balance âgée
// -----------------------------------------------------------------------------

/**
 * LES TRANCHES, par quinzaine — demandées par l'utilisateur le 09/09/2026 :
 * « le montant des sommes en retard par 15aines ».
 */
export const TRANCHES: readonly {
  readonly cle: string;
  readonly nom: string;
  readonly de: number;
  readonly a: number;
}[] = [
  { cle: '1-15', nom: '1 – 15 j', de: 1, a: 15 },
  { cle: '16-30', nom: '16 – 30 j', de: 16, a: 30 },
  { cle: '31-45', nom: '31 – 45 j', de: 31, a: 45 },
  { cle: '46-60', nom: '46 – 60 j', de: 46, a: 60 },
  { cle: '60+', nom: '+ de 60 j', de: 61, a: Number.POSITIVE_INFINITY },
];

/**
 * DANS QUELLE COLONNE TOMBE UNE PIÈCE.
 *
 * `null` d'échéance vaut « à échoir » : une pièce sans échéance n'est pas en
 * retard, elle est sans terme. La traiter comme échue ferait relancer une
 * facture dont on n'a jamais fixé la date.
 */
export function trancheDe(echeance: string | null, aujourdhui: string): string {
  if (echeance === null) return 'a-echoir';
  const retard = ecartJours(echeance, aujourdhui);
  if (retard === null || retard <= 0) return 'a-echoir';
  const tranche = TRANCHES.find((t) => retard >= t.de && retard <= t.a);
  return tranche?.cle ?? '60+';
}

/** Une ligne de la balance âgée : un client, ses colonnes, son état. */
export interface LigneBalance {
  readonly clientId: string;
  readonly clientNom: string;
  /** Une entrée par clé de tranche, plus `a-echoir`. En centimes. */
  readonly colonnes: Readonly<Record<string, number>>;
  /** Ce qu'il a versé sans imputation. Négatif. */
  readonly acompte: number;
  /** La somme des colonnes et de l'acompte. */
  readonly total: number;
  /** Ce qui est échu, acompte non déduit. */
  readonly echu: number;
  /**
   * LES GELS EN COURS SUR SES PIÈCES. Vide quand il n'y en a aucun.
   *
   * Une liste et non un gel unique : depuis le 10/09/2026 le gel porte sur la
   * facture, donc un client peut en avoir plusieurs, avec des dates de reprise
   * différentes.
   */
  readonly gels: readonly Gel[];
  /** Le niveau le plus avancé RÉELLEMENT parti pour une de ses pièces. */
  readonly niveau: string | null;
  readonly niveauLe: string | null;
  readonly pieces: readonly PieceRecouvrement[];
  /**
   * SON RETARD MOYEN DE RÈGLEMENT, en jours après échéance, sur les pièces
   * SOLDÉES — y compris celles qui ne figurent plus dans les colonnes.
   * `null` s'il n'a encore rien payé.
   */
  readonly retardMoyen: number | null;
}

/**
 * LA BALANCE ÂGÉE.
 *
 * Un client par ligne, ses pièces non soldées ventilées par ancienneté, son
 * acompte en négatif. **Chaque ligne s'additionne à l'horizontale** — c'est la
 * seule façon de faire confiance à un tableau de soldes.
 *
 * Un client dont tout est soldé et qui n'a pas d'acompte DISPARAÎT. Le garder
 * à zéro allongerait la liste de ce qu'on n'a pas à regarder.
 *
 * LE TRI PAR DÉFAUT EST LE MONTANT ÉCHU, DÉCROISSANT — validé le 10/09/2026.
 * Pas l'ordre alphabétique : on n'ouvre pas cette page pour chercher un client,
 * on l'ouvre pour savoir qui doit le plus depuis le plus longtemps.
 */
export function balanceAgee(
  pieces: readonly PieceRecouvrement[],
  acomptes: readonly Acompte[],
  gels: readonly Gel[],
  aujourdhui: string,
): readonly LigneBalance[] {
  const vides = (): Record<string, number> => {
    const colonnes: Record<string, number> = { 'a-echoir': 0 };
    for (const tranche of TRANCHES) colonnes[tranche.cle] = 0;
    return colonnes;
  };

  interface EnCours {
    clientId: string;
    clientNom: string;
    colonnes: Record<string, number>;
    acompte: number;
    pieces: PieceRecouvrement[];
    niveau: string | null;
    niveauLe: string | null;
  }

  const parClient = new Map<string, EnCours>();
  const ouvrir = (id: string, nom: string): EnCours => {
    const connu = parClient.get(id);
    if (connu !== undefined) return connu;
    const neuf: EnCours = {
      clientId: id,
      clientNom: nom,
      colonnes: vides(),
      acompte: 0,
      pieces: [],
      niveau: null,
      niveauLe: null,
    };
    parClient.set(id, neuf);
    return neuf;
  };

  /**
   * LES PIÈCES SOLDÉES SERVENT AU RETARD MOYEN, et à rien d'autre : elles ne
   * pèsent dans aucune colonne. On les collecte avant la boucle principale,
   * qui, elle, ignore tout ce qui est soldé.
   */
  const soldeesParClient = new Map<string, PieceRecouvrement[]>();
  for (const piece of pieces) {
    if (delaiDeReglement(piece) === null) continue;
    const dejaLa = soldeesParClient.get(piece.client_id);
    if (dejaLa === undefined) soldeesParClient.set(piece.client_id, [piece]);
    else dejaLa.push(piece);
  }

  for (const piece of pieces) {
    const reste = resteDu(piece);
    if (reste === 0) continue;
    const ligne = ouvrir(piece.client_id, piece.client_nom);
    const colonne = trancheDe(piece.echeance, aujourdhui);
    ligne.colonnes[colonne] = (ligne.colonnes[colonne] ?? 0) + reste;
    ligne.pieces.push(piece);

    // LE NIVEAU LE PLUS AVANCÉ parmi ses pièces. Le niveau est PAR PIÈCE ;
    // ce que la ligne client affiche est le plus loin où l'on soit allé.
    if (piece.relance_niveau !== null) {
      const rangConnu =
        ligne.niveau === null ? -1 : CADENCE.findIndex((n) => n.cle === ligne.niveau);
      const rang = CADENCE.findIndex((n) => n.cle === piece.relance_niveau);
      if (rang > rangConnu) {
        ligne.niveau = piece.relance_niveau;
        ligne.niveauLe = piece.relance_le;
      }
    }
  }

  for (const acompte of acomptes) {
    const somme = centimes(acompte.montant);
    if (somme === 0) continue;
    const ligne = ouvrir(acompte.client_id, acompte.client_nom);
    ligne.acompte -= somme;
  }

  // UN CLIENT PEUT AVOIR PLUSIEURS PIÈCES GELÉES, chacune avec sa date de
  // reprise et son motif — depuis que le gel porte sur la facture (10/09/2026).
  const gelsParClient = new Map<string, Gel[]>();
  for (const gel of gels) {
    const dejaLa = gelsParClient.get(gel.client_id);
    if (dejaLa === undefined) gelsParClient.set(gel.client_id, [gel]);
    else dejaLa.push(gel);
  }

  const lignes: LigneBalance[] = [...parClient.values()].map((c) => {
    const echu = TRANCHES.reduce((somme, t) => somme + (c.colonnes[t.cle] ?? 0), 0);
    const total = echu + (c.colonnes['a-echoir'] ?? 0) + c.acompte;
    return {
      clientId: c.clientId,
      clientNom: c.clientNom,
      colonnes: c.colonnes,
      acompte: c.acompte,
      total,
      echu,
      gels: gelsParClient.get(c.clientId) ?? [],
      niveau: c.niveau,
      niveauLe: c.niveauLe,
      pieces: c.pieces,
      retardMoyen: retardMoyen(soldeesParClient.get(c.clientId) ?? []),
    };
  });

  return lignes
    .filter((l) => l.total !== 0 || l.echu !== 0 || l.acompte !== 0)
    .sort(
      (a, b) =>
        b.echu - a.echu || b.total - a.total || a.clientNom.localeCompare(b.clientNom),
    );
}

/** Les totaux de bas de tableau — colonne par colonne, plus le total général. */
export function totauxBalance(lignes: readonly LigneBalance[]): {
  readonly colonnes: Readonly<Record<string, number>>;
  readonly acompte: number;
  readonly total: number;
  readonly echu: number;
} {
  const colonnes: Record<string, number> = { 'a-echoir': 0 };
  for (const tranche of TRANCHES) colonnes[tranche.cle] = 0;
  let acompte = 0;
  let total = 0;
  let echu = 0;
  for (const ligne of lignes) {
    for (const cle of Object.keys(colonnes)) {
      colonnes[cle] = (colonnes[cle] ?? 0) + (ligne.colonnes[cle] ?? 0);
    }
    acompte += ligne.acompte;
    total += ligne.total;
    echu += ligne.echu;
  }
  return { colonnes, acompte, total, echu };
}

// -----------------------------------------------------------------------------
// Les relances
// -----------------------------------------------------------------------------

/** Ce qu'une pièce attend : le niveau suivant celui qui est parti, et sa date. */
export interface NiveauDu {
  readonly cle: string;
  readonly nom: string;
  readonly canal: string;
  /** Le jour où ce courrier devient dû. */
  readonly dueLe: string;
  /** Jours écoulés depuis. Zéro le jour même, négatif si pas encore dû. */
  readonly retard: number;
}

/**
 * LE NIVEAU DÛ POUR UNE PIÈCE — la règle corrigée le 10/09/2026.
 *
 * **Ce n'est PAS le niveau que la date autorise, c'est LE SUIVANT DE CELUI QUI
 * EST RÉELLEMENT PARTI.** La date donne le plus tôt, pas le droit de sauter.
 *
 * Sans cette règle, une facture oubliée trois semaines partait directement en
 * action directe sans mise en demeure : juridiquement bancal — on n'exerce pas
 * l'action directe sans avoir formellement mis en demeure — et commercialement
 * suicidaire.
 *
 * Conséquence assumée : une facture en retard de cinq niveaux met cinq envois à
 * rattraper. C'est une question ouverte de la maquette, pas un oubli.
 *
 * `null` quand il n'y a plus rien à envoyer : pièce soldée, sans échéance, ou
 * déjà allée jusqu'au bout de la cadence.
 */
export function niveauDuDeLaPiece(
  piece: PieceRecouvrement,
  aujourdhui: string,
): NiveauDu | null {
  if (resteDu(piece) <= 0) return null;
  if (piece.echeance === null) return null;

  const rangParti =
    piece.relance_niveau === null
      ? -1
      : CADENCE.findIndex((n) => n.cle === piece.relance_niveau);

  /**
   * LA SEULE EXCEPTION À « ON NE SAUTE JAMAIS UN NIVEAU », et c'est un test qui
   * l'a trouvée le 10/09/2026, pas moi.
   *
   * L'avis de compte tombe AVANT l'échéance : c'est un contrôle de
   * comptabilisation, pas une relance. Sans cette exception, une facture que
   * personne n'a relancée se voyait réclamer un avis de compte treize jours
   * APRÈS son échéance — un courrier qui dit « votre facture arrive à
   * échéance » alors qu'elle est en retard.
   *
   * On ne rattrape donc jamais un niveau antérieur à l'échéance une fois
   * l'échéance passée. Tous les autres se rattrapent, dans l'ordre.
   */
  let rang = rangParti + 1;
  const echu = (ecartJours(piece.echeance, aujourdhui) ?? 0) > 0;
  while (echu && (CADENCE[rang]?.jour ?? 0) < 0) rang += 1;

  const suivant = CADENCE[rang];
  if (suivant === undefined) return null;

  const dueLe = ajouterJours(piece.echeance, suivant.jour);
  if (dueLe === null) return null;
  const retard = ecartJours(dueLe, aujourdhui);
  if (retard === null) return null;

  return {
    cle: suivant.cle,
    nom: suivant.nom,
    canal: suivant.canal,
    dueLe,
    retard,
  };
}

/**
 * UN COURRIER À ENVOYER : un client, un niveau, et les pièces qu'il portera.
 *
 * LE COURRIER EST PAR CLIENT, LE NIVEAU PAR PIÈCE — garde-fou du 31/07/2026 :
 * « plusieurs factures d'un même client au même niveau le même jour partent
 * dans un seul courrier ».
 */
export interface CourrierARelancer {
  readonly clef: string;
  readonly clientId: string;
  readonly clientNom: string;
  readonly niveau: string;
  readonly niveauNom: string;
  readonly canal: string;
  /** Le jour où le courrier devient dû : le PLUS TARD de ses pièces. */
  readonly dueLe: string;
  readonly retard: number;
  readonly montant: number;
  readonly pieces: readonly PieceRecouvrement[];
  readonly gel: Gel | null;
  /** `retard` et `dueLe` sont indépendants du gel : un gel n'efface rien. */
  readonly etat: 'retard' | 'jour' | 'a-venir' | 'gele';
}

/**
 * LA FILE DES RELANCES.
 *
 * Trois états, dans l'ordre où ils comptent : ce qui aurait dû partir, ce qui
 * part aujourd'hui, ce qui est gelé. Ce qui n'est pas encore dû n'y figure pas
 * — c'est la liste de ce qu'on fait, pas de ce qu'on fera.
 *
 * UNE PIÈCE GELÉE RESTE DANS LA FILE, à part. Un gel arrête la cadence, il
 * n'efface pas la dette : le cacher, c'est le perdre.
 *
 * LE GEL PORTE SUR LA PIÈCE — corrigé le 10/09/2026. Une pièce gelée fait donc
 * SON PROPRE courrier, elle ne se groupe pas : deux pièces gelées du même
 * client peuvent avoir deux motifs et deux dates de reprise, et les mettre
 * ensemble obligerait à en choisir une. Les pièces NON gelées se groupent
 * normalement, ce qui est tout l'intérêt du changement : un client qui conteste
 * une facture continue d'être relancé sur les autres.
 */
export function fileDeRelance(
  pieces: readonly PieceRecouvrement[],
  gels: readonly Gel[],
  aujourdhui: string,
): readonly CourrierARelancer[] {
  const parPiece = new Map(gels.map((g) => [g.facture_id, g]));
  const gelActif = (piece: PieceRecouvrement): Gel | null => {
    const gel = parPiece.get(piece.id);
    if (gel === undefined) return null;
    return gel.reprise_le > aujourdhui ? gel : null;
  };

  interface EnCours {
    clientId: string;
    clientNom: string;
    niveau: string;
    niveauNom: string;
    canal: string;
    dueLe: string;
    montant: number;
    pieces: PieceRecouvrement[];
    gel: Gel | null;
  }
  const paquets = new Map<string, EnCours>();

  for (const piece of pieces) {
    const du = niveauDuDeLaPiece(piece, aujourdhui);
    if (du === null) continue;
    // PAS ENCORE DÛ : hors file.
    if (du.retard < 0) continue;

    // UNE PIÈCE GELÉE NE SE GROUPE PAS : sa clé porte son propre identifiant.
    const gel = gelActif(piece);
    const clef =
      gel === null ? `${piece.client_id}#${du.cle}` : `${piece.id}#${du.cle}#gel`;
    const connu = paquets.get(clef);
    if (connu === undefined) {
      paquets.set(clef, {
        clientId: piece.client_id,
        clientNom: piece.client_nom,
        niveau: du.cle,
        niveauNom: du.nom,
        canal: du.canal,
        dueLe: du.dueLe,
        montant: resteDu(piece),
        pieces: [piece],
        gel,
      });
    } else {
      connu.pieces.push(piece);
      connu.montant += resteDu(piece);
      // LE COURRIER NE PART QUE QUAND TOUTES SES PIÈCES ONT ATTEINT LE NIVEAU :
      // sa date due est donc la PLUS TARDIVE, pas la plus ancienne.
      if (connu.dueLe < du.dueLe) connu.dueLe = du.dueLe;
    }
  }

  const courriers: CourrierARelancer[] = [...paquets.entries()].map(([clef, p]) => {
    const retard = ecartJours(p.dueLe, aujourdhui) ?? 0;
    return {
      clef,
      clientId: p.clientId,
      clientNom: p.clientNom,
      niveau: p.niveau,
      niveauNom: p.niveauNom,
      canal: p.canal,
      dueLe: p.dueLe,
      retard,
      montant: p.montant,
      pieces: p.pieces,
      gel: p.gel,
      etat:
        p.gel !== null
          ? 'gele'
          : retard > 0
            ? 'retard'
            : retard === 0
              ? 'jour'
              : 'a-venir',
    };
  });

  const rang = { retard: 0, jour: 1, gele: 2, 'a-venir': 3 };
  return courriers.sort(
    (a, b) =>
      rang[a.etat] - rang[b.etat] ||
      b.retard - a.retard ||
      a.clientNom.localeCompare(b.clientNom),
  );
}

// -----------------------------------------------------------------------------
// La saisie d'un encaissement
// -----------------------------------------------------------------------------

/** Ce qui reste à affecter, et si l'on peut enregistrer. */
export interface Compteur {
  /** Reçu moins affecté, en centimes. Zéro quand tout est ventilé. */
  readonly reste: number;
  readonly recu: number;
  readonly affecte: number;
  /** `juste` à zéro, `acompte` s'il reste, `trop` si l'on dépasse. */
  readonly etat: 'juste' | 'acompte' | 'trop';
  /** Le refus est la seule chose que le compteur commande. */
  readonly enregistrable: boolean;
}

/**
 * LE COMPTEUR DE LA FENÊTRE DE SAISIE.
 *
 * Vert à zéro, orange s'il reste — le reste part en ACOMPTE sur le compte du
 * client, validé le 09/09 — rouge si l'on affecte plus qu'on n'a reçu, et là on
 * n'enregistre pas. C'est une faute de frappe, jamais une intention.
 */
export function compteur(
  recuSaisi: string | number,
  affectations: readonly { readonly montant: string | number }[],
): Compteur {
  const recu = centimes(recuSaisi);
  const affecte = affectations.reduce((somme, a) => somme + centimes(a.montant), 0);
  const reste = recu - affecte;
  return {
    reste,
    recu,
    affecte,
    etat: reste === 0 ? 'juste' : reste > 0 ? 'acompte' : 'trop',
    enregistrable: reste >= 0 && recu > 0,
  };
}

/**
 * AFFECTER AU PLUS ANCIEN — validé le 10/09/2026, en un bouton.
 *
 * C'est la règle d'imputation ordinaire, et ce qu'on fait à la main neuf fois
 * sur dix. Le bouton ne fait que remplir les cases : rien n'est écrit tant
 * qu'on n'a pas enregistré.
 *
 * LES AVOIRS PASSENT EN PREMIER, en entier. Un avoir réduit ce que le client
 * doit ; l'oublier ferait réclamer trop. Il augmente au passage ce qu'il reste
 * à imputer sur les factures — c'est exactement le cas de la maquette :
 * 2 000 reçus + 340 d'avoir = 2 340 imputés.
 *
 * Puis les factures, de la plus ancienne échéance à la plus récente, chacune à
 * hauteur de ce qui lui reste dû et de ce qui reste en caisse.
 */
export function affecterAuPlusAncien(
  recuSaisi: string | number,
  pieces: readonly PieceRecouvrement[],
): readonly { readonly facture_id: string; readonly montant: number }[] {
  const proposees: { facture_id: string; montant: number }[] = [];
  let disponible = centimes(recuSaisi);

  const ouvertes = pieces.filter((p) => resteDu(p) !== 0);

  for (const avoir of ouvertes.filter((p) => p.type === 'avoir')) {
    const reste = resteDu(avoir);
    if (reste >= 0) continue;
    proposees.push({ facture_id: avoir.id, montant: reste });
    disponible -= reste;
  }

  const factures = ouvertes
    .filter((p) => p.type !== 'avoir' && resteDu(p) > 0)
    .sort((a, b) =>
      (a.echeance ?? a.date_piece).localeCompare(b.echeance ?? b.date_piece),
    );

  for (const facture of factures) {
    if (disponible <= 0) break;
    const pris = Math.min(disponible, resteDu(facture));
    if (pris <= 0) continue;
    proposees.push({ facture_id: facture.id, montant: pris });
    disponible -= pris;
  }

  return proposees;
}

// -----------------------------------------------------------------------------
// Les décomptes affichés
// -----------------------------------------------------------------------------

/** La ligne de décompte du poste client. */
export function decomptePoste(lignes: readonly LigneBalance[]): string {
  const totaux = totauxBalance(lignes);
  const bouts = [
    `${String(lignes.length)} client${lignes.length > 1 ? 's' : ''}`,
    `${montant(totaux.total)} € d’encours`,
  ];
  if (totaux.echu !== 0) bouts.push(`dont ${montant(totaux.echu)} € échus`);
  // ON COMPTE DES PIÈCES, plus des clients : le gel porte sur la facture.
  const geles = lignes.reduce((somme, l) => somme + l.gels.length, 0);
  if (geles > 0)
    bouts.push(`${String(geles)} pièce${geles > 1 ? 's gelées' : ' gelée'}`);
  if (totaux.acompte !== 0) {
    bouts.push(`${montant(-totaux.acompte)} € d’acomptes non affectés`);
  }
  return bouts.join(' · ');
}

/** La ligne de décompte de la file des relances. */
export function decompteRelances(courriers: readonly CourrierARelancer[]): string {
  const enRetard = courriers.filter((c) => c.etat === 'retard');
  const duJour = courriers.filter((c) => c.etat === 'jour');
  const geles = courriers.filter((c) => c.etat === 'gele');
  const pieces = courriers.reduce((somme, c) => somme + c.pieces.length, 0);
  return [
    `${String(courriers.length)} courrier${courriers.length > 1 ? 's' : ''}`,
    `pour ${String(pieces)} pièce${pieces > 1 ? 's' : ''}`,
    enRetard.length > 0
      ? `${String(enRetard.length)} en retard d’envoi`
      : 'aucun en retard',
    duJour.length > 0 ? `${String(duJour.length)} dû ce jour` : 'aucun dû ce jour',
    `${String(geles.length)} gelé${geles.length > 1 ? 's' : ''}`,
  ].join(' · ');
}

/** Le décompte du journal des encaissements. */
export function decompteEncaissements(encaissements: readonly Encaissement[]): string {
  const recu = encaissements.reduce((somme, e) => somme + centimes(e.montant), 0);
  const nonAffecte = encaissements.reduce(
    (somme, e) => somme + (centimes(e.montant) - centimes(e.affecte)),
    0,
  );
  const bouts = [
    `${String(encaissements.length)} encaissement${encaissements.length > 1 ? 's' : ''}`,
    `${montant(recu)} € reçus`,
  ];
  if (nonAffecte !== 0) bouts.push(`${montant(nonAffecte)} € non affectés`);
  return bouts.join(' · ');
}

/** Ce que la colonne « Affectation » du journal affiche. */
export function affectationDite(encaissement: Encaissement): {
  readonly mot: string;
  readonly etat: 'solde' | 'partiel' | 'libre';
} {
  const reste = centimes(encaissement.montant) - centimes(encaissement.affecte);
  if (encaissement.affectations.length === 0) {
    return { mot: 'Non affecté', etat: 'libre' };
  }
  if (reste === 0) return { mot: 'Tout affecté', etat: 'solde' };
  return { mot: `Reste ${montant(reste)} en acompte`, etat: 'partiel' };
}
